import { DynamicWithdrawalActivityCTO, PoolDynamicOverviewCTO } from '@shared/sharedTypes'
import React, { useEffect, useState } from 'react'

import { SectionHeader } from '@react/components/common/SectionHeader'
import { OtStoreState, useOtChainState, useOtSelector } from '@react/redux/store'
import * as yup from 'yup'

import { Box, Grid, Typography, useTheme } from '@mui/material'
import { AddressPill } from '@react/components/addresses/AddressPill'
import ButtonModal from '@react/components/ButtonModal'
import ViewNumber from '@react/components/elements/ViewNumber'
import AdminDynamicRepayRedeemForm from '@react/dynamic/AdminDynamicRepayRedeemForm'

import { fetchDynamicPoolWithdrawActivitiesCTOs, scanPoolsForEvents } from '@react/fetchers/PoolFetchers'
import { ERC20Wei, erc20WeiToCommaDecString, erc20WeiToNumber, otAssert } from '@shared/conversions'

import { RoleEnum, yupErc20WeiRequired } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'

import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import AdminDynamicWithdrawDestinationChangeForm from '@react/dynamic/AdminDynamicWithdrawDestinationChangeForm'
import { usePoolLiquidityBalance } from '@react/hooks'
import { repayLytWithdraw } from '@react/signedTxs/repayLytWithdrawTx'
import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { ETH_ADDR_ZERO, EthAddr, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import { useParams } from 'react-router-dom'

export const LytWithdrawalsActionList: React.FC<{ poolAddr?: EthAddr }> = (props) => {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { roleEnum, signer },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  const chainState = useOtSelector((state: OtStoreState) => state.chainState)
  otAssert(providerReadOnly, 'providerReadOnly is not set')
  const poolAddr = (props.poolAddr ? props.poolAddr : useParams().poolAddr) as EthAddr
  const companyProfiles = useOtSelector((state: OtStoreState) => state.poolState.companyProfiles)
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null)
  const [selectedActivityId, setSelectedActivityId] = React.useState<string | null>(null)

  const theme = useTheme()

  const poolDynamicOverviewCTO: PoolDynamicOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  ) as PoolDynamicOverviewCTO
  const [withdrawActivityCTOs, setWithdrawActivityCTOs] = useState<DynamicWithdrawalActivityCTO[]>([])
  const [totalPendingWithdraws, setTotalPendingWithdraws] = useState<number>(0)

  const {
    poolBalance,
    isLoading,
    error: balanceError,
  } = usePoolLiquidityBalance({
    poolAddr,
    poolOverview: poolDynamicOverviewCTO,
    enabled: !!poolDynamicOverviewCTO,
    refetchInterval: 30000,
  })

  useEffect(() => {
    async function fetchWithdrawActivities(): Promise<void> {
      if (providerReadOnly && signer && serverApiBaseUrl && restAPIKey && poolAddr) {
        logOT.info(' fetchWithdraws  Start ')
        await scanPoolsForEvents(serverApiBaseUrl, restAPIKey, poolAddr)
        const allPoolActivities = await fetchDynamicPoolWithdrawActivitiesCTOs(serverApiBaseUrl, restAPIKey, poolAddr)

        setWithdrawActivityCTOs(allPoolActivities ?? [])
        logOT.info(' fetchWithdraws Returned requests', allPoolActivities)

        const totalPendingWithdraws = allPoolActivities.reduce((acc, activity) => {
          const assets = Math.round(100 * erc20WeiToNumber(activity.assets as ERC20Wei)) / 100
          return acc + assets
        }, 0)
        setTotalPendingWithdraws(Math.round(totalPendingWithdraws * 1e6))
      }
    }

    fetchWithdrawActivities().catch((e) => {
      logOT.info('error', e)
    })
  }, [poolDynamicOverviewCTO, providerReadOnly, signer, serverApiBaseUrl, restAPIKey, poolAddr])

  if (!poolDynamicOverviewCTO) {
    return <></>
  }

  const topPanelSchema = yup.object({
    totalPendingWithdraws: yupErc20WeiRequired.label('Assets to be Withdrawn').meta(
      toFieldMetaData({
        propName: 'totalPendingWithdraws',
        description: 'The amount of Loan Assets at Maturity that is currently in outstanding Early Withdrawal Requests',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    poolBalance: yupErc20WeiRequired.label('The amount of Liquidity Assets held by the Pool').meta(
      toFieldMetaData({
        propName: 'poolBalance',
        description: 'The amount of Liquidity Assets held by the Pool',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  })
  const getCompanyProfile = (userAddr: EthAddr): CompanyProfileCTO => {
    const companyProfile = companyProfiles.find((profile) => profile.wallets.includes(userAddr))

    return (
      companyProfile ?? {
        instructions: '',
        companyName: 'Unlisted',
        uuid: '',
        wallets: [],
        emails: [],
      }
    )
  }

  const handleRepayLytWithdraw = async (args: {
    activity: DynamicWithdrawalActivityCTO
    amount: ERC20Wei
    fees: ERC20Wei
  }): Promise<void> => {
    const { webSocket } = chainState
    const { chainConfigurationName } = chainState.chainConfiguration
    otAssert(signer, 'Signer is not available')

    await repayLytWithdraw(webSocket, signer, args.activity, args.amount, args.fees, chainConfigurationName)
  }

  const liquidityTokenSymbol = poolDynamicOverviewCTO.liquidityTokenSymbol

  return (
    <Box sx={{ paddingTop: 2 }}>
      <SectionHeader>Withdrawal Requests</SectionHeader>
      <Box sx={{ padding: 4, marginBottom: 4 }}>
        <Grid
          container
          spacing={2}
          sx={{ borderBottomWidth: 1, borderBottomColor: theme.palette.divider, marginTop: 2 }}
        >
          <Grid item sx={{ marginBottom: 4 }} xs={12} sm={6}>
            <ViewNumber
              card
              toolTip="The amount of Loan Assets at Maturity that is currently in outstanding Early Withdrawal Requests"
              label="Assets to be Withdrawn"
              inTable={false}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol=""
              dataValue={`${totalPendingWithdraws}`}
              fieldSchema={topPanelSchema.describe().fields.totalPendingWithdraws}
              value={`${totalPendingWithdraws}`}
            />
          </Grid>
          <Grid item sx={{ marginBottom: 4 }} xs={12} sm={6}>
            <ViewNumber
              card
              toolTip="The amount of Liquidity Assets held by the Pool"
              label="Pool Balance"
              inTable={false}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol=""
              dataValue={`${poolBalance}`}
              fieldSchema={topPanelSchema.describe().fields.poolBalance}
              value={`${poolBalance}`}
            />
            {balanceError && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                Error loading balance: {balanceError.message}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
      <TableContainer sx={{ maxWidth: '100%' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="20%">Lender</TableCell>
              <TableCell width="30%">Shares</TableCell>
              <TableCell width="20%">Destination</TableCell>
              <TableCell width="20%">Principal</TableCell>
              <TableCell width="20%" align="center">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {withdrawActivityCTOs.map((activity: DynamicWithdrawalActivityCTO) => (
              <TableRow key={activity.uuid}>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {getCompanyProfile(activity.lender).companyName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddressPill addr={activity.lender} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="bodyBold">{erc20WeiToCommaDecString(activity.shares)}</Typography>
                </TableCell>
                <TableCell>
                  {activity.fundsDestination &&
                    getCompanyProfile(activity.fundsDestination).uuid !== getCompanyProfile(activity.lender).uuid && (
                      <Typography color="error">MISS MATCHED COMPANIES</Typography>
                    )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {activity.fundsDestination && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          {getCompanyProfile(activity.fundsDestination).companyName}
                        </Typography>
                        <AddressPill addr={activity.fundsDestination as string} />
                      </>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {getCompanyProfile(activity.lender).instructions}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="bodyBold">{erc20WeiToCommaDecString(activity.assets)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <ButtonModal
                      title={'Repay Withdrawal'}
                      variant="primary"
                      disabled={
                        roleEnum !== RoleEnum.ISSUER || isLoading || poolBalance < BigInt(activity.assets as ERC20Wei)
                      }
                      tooltip={
                        roleEnum !== RoleEnum.ISSUER
                          ? 'Only issuers can repay withdrawals'
                          : isLoading
                            ? 'Pool balance is loading...'
                            : poolBalance < BigInt(activity.assets as ERC20Wei)
                              ? 'Insufficient pool balance to repay this withdrawal'
                              : undefined
                      }
                    >
                      <AdminDynamicRepayRedeemForm
                        activity={activity}
                        symbol={poolDynamicOverviewCTO.symbol}
                        liquidityTokenSymbol={poolDynamicOverviewCTO.liquidityTokenSymbol}
                        repayWithdrawFn={handleRepayLytWithdraw}
                      />
                    </ButtonModal>
                    {roleEnum === RoleEnum.ISSUER && (
                      <>
                        <IconButton
                          onClick={(event) => {
                            setMenuAnchorEl(event.currentTarget)
                            setSelectedActivityId(activity.uuid)
                          }}
                          disabled={roleEnum !== RoleEnum.ISSUER}
                          sx={{ p: 1 }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={menuAnchorEl}
                          open={Boolean(menuAnchorEl) && selectedActivityId === activity.uuid}
                          onClose={() => {
                            setMenuAnchorEl(null)
                            setSelectedActivityId(null)
                          }}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <MenuItem>
                            <ButtonModal
                              title={'Change Destination Wallet'}
                              variant="primary"
                              disabled={roleEnum !== RoleEnum.ISSUER}
                              sx={{ p: 0, minWidth: 'auto' }}
                            >
                              <AdminDynamicWithdrawDestinationChangeForm
                                activity={activity}
                                symbol={poolDynamicOverviewCTO.symbol}
                                liquidityTokenSymbol={poolDynamicOverviewCTO.liquidityTokenSymbol}
                              />
                            </ButtonModal>
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {withdrawActivityCTOs.length === 0 && (
        <Box sx={{ padding: 4 }}>
          <Typography variant="bodyMuted">No Token transfer Withdrawal Requests at this time.</Typography>
        </Box>
      )}
    </Box>
  )
}
